// Notification center — bell icon with unread badge + drawer that lists
// all recent in-app notifications chronologically. Uses the same
// SidePanel bottom-sheet pattern: slide from right on desktop, bottom
// sheet on mobile.

import { AlertTriangle, Bell, CheckCircle2, Info, Trash2, X, XCircle } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import type { AppNotification } from '../lib/notification-store'
import { useNotificationStore } from '../lib/notification-store'
import { useOverlayStore } from '../lib/overlay-store'
import { useIsMobile } from '../lib/use-media-query'
import { useSheetMaxHeight } from '../lib/use-visual-viewport'
import { cn } from '../lib/utils'
import { EmptyState } from './ui/empty-state'

// ── Bell button (embed in header / sidebar footer) ──────────────

export function NotificationBell({
  onClick,
  className,
}: {
  onClick: () => void
  className?: string
}) {
  const unreadCount = useNotificationStore((s) => s.unreadCount)

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Benachrichtigungen${unreadCount > 0 ? ` (${unreadCount} ungelesen)` : ''}`}
      className={cn(
        'relative p-2 rounded-md text-text-tertiary hover:text-text-primary hover:bg-bg-overlay transition-colors touch:opacity-100',
        className,
      )}
    >
      <Bell className="h-4 w-4" />
      {unreadCount > 0 && (
        <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-accent px-1 text-2xs font-semibold leading-none text-bg-base tabular-nums no-min-tap">
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}
    </button>
  )
}

// ── Drawer / panel ──────────────────────────────────────────────

interface NotificationCenterProps {
  open: boolean
  onClose: () => void
}

export function NotificationCenter({ open, onClose }: NotificationCenterProps) {
  const notifications = useNotificationStore((s) => s.notifications)
  const markAllRead = useNotificationStore((s) => s.markAllRead)
  const clear = useNotificationStore((s) => s.clear)
  const dismiss = useNotificationStore((s) => s.dismiss)
  const isMobile = useIsMobile()
  // Sheet-Höhe am sichtbaren Viewport (identisch zu dialog/side-panel) —
  // Umzug aufs Sheet-Primitive ist ein späteres Refactoring.
  const sheetMaxHeight = useSheetMaxHeight()
  const contentRef = useRef<HTMLDivElement>(null)

  // Track in global overlay store so floating UI can hide.
  const pushOverlay = useOverlayStore((s) => s.push)
  const popOverlay = useOverlayStore((s) => s.pop)
  useEffect(() => {
    if (!open) return
    pushOverlay()
    return () => popOverlay()
  }, [open, pushOverlay, popOverlay])

  // Escape schließt (Muster ui/dialog.tsx) — gilt für Sheet und Desktop-Panel.
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose])

  if (!open) return null
  if (typeof document === 'undefined') return null

  const handleMarkAllAndClear = () => {
    markAllRead()
    clear()
  }

  const content = (
    <>
      {/* Header */}
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border-subtle px-4 py-3">
        <h2 className="text-lg font-medium leading-tight text-text-primary">Benachrichtigungen</h2>
        <div className="flex items-center gap-1">
          {notifications.length > 0 && (
            <button
              type="button"
              onClick={handleMarkAllAndClear}
              className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs text-text-tertiary hover:text-text-primary hover:bg-bg-overlay transition-colors no-min-tap"
            >
              <Trash2 className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Alle löschen</span>
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            className="shrink-0 p-2 rounded-md text-text-tertiary hover:text-text-primary hover:bg-bg-overlay/60 touch:opacity-100 no-min-tap"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* List */}
      <div
        ref={contentRef}
        className="flex-1 min-h-0 overflow-y-auto overscroll-contain scrollbar-thin"
      >
        {notifications.length === 0 ? (
          <EmptyState icon={Bell} message="Keine Benachrichtigungen" className="py-16" />
        ) : (
          <div className="divide-y divide-border-subtle">
            {notifications.map((n) => (
              <NotificationRow key={n.id} notification={n} onDismiss={dismiss} />
            ))}
          </div>
        )}
      </div>
    </>
  )

  // ── Mobile: bottom sheet ──
  if (isMobile) {
    return createPortal(
      <div className="fixed inset-0 z-50">
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: backdrop dismiss */}
        {/* biome-ignore lint/a11y/noStaticElementInteractions: backdrop dismiss */}
        <div className="absolute inset-0 bg-black/50 animate-fade-in" onClick={onClose} />
        <div
          className="app-safe-inline absolute bottom-0 left-0 right-0 bg-bg-elevated rounded-t-2xl flex flex-col animate-slide-up"
          role="dialog"
          aria-modal="true"
          aria-label="Benachrichtigungen"
          style={{
            maxHeight: sheetMaxHeight,
            paddingBottom: 'var(--app-safe-bottom)',
          }}
        >
          {/* Drag handle */}
          <div className="flex justify-center py-2 shrink-0">
            <div className="w-10 h-1 rounded-full bg-text-tertiary/30" />
          </div>
          {content}
        </div>
      </div>,
      document.body,
    )
  }

  // ── Desktop: slide from right ──
  return createPortal(
    <div
      className={cn(
        'app-safe-right-panel fixed top-0 right-0 bottom-0 z-40 flex flex-col',
        'w-full sm:w-[380px] lg:w-[420px]',
        'border-l border-border-default bg-bg-elevated shadow-2xl',
        'animate-slide-in-right',
      )}
      role="dialog"
      aria-modal="false"
      aria-label="Benachrichtigungen"
    >
      {content}
    </div>,
    document.body,
  )
}

// ── Single notification row ─────────────────────────────────────

const TYPE_META: Record<
  AppNotification['type'],
  { icon: typeof CheckCircle2; colorClass: string }
> = {
  success: { icon: CheckCircle2, colorClass: 'text-success' },
  error: { icon: XCircle, colorClass: 'text-danger' },
  info: { icon: Info, colorClass: 'text-info' },
  warning: { icon: AlertTriangle, colorClass: 'text-warning' },
}

function NotificationRow({
  notification: n,
  onDismiss,
}: {
  notification: AppNotification
  onDismiss: (id: string) => void
}) {
  const meta = TYPE_META[n.type]
  const Icon = meta.icon
  const timeStr = n.timestamp.toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
  })

  return (
    <div
      className={cn(
        'group flex items-start gap-3 px-4 py-3 transition-colors',
        !n.read && 'bg-accent/[0.04]',
      )}
    >
      <Icon className={cn('h-4 w-4 mt-0.5 shrink-0', meta.colorClass)} />
      <div className="min-w-0 flex-1">
        <p className="text-sm text-text-primary leading-snug break-words">{n.message}</p>
        <p className="mt-0.5 text-[11px] text-text-tertiary tabular-nums">{timeStr}</p>
      </div>
      <button
        type="button"
        onClick={() => onDismiss(n.id)}
        aria-label="Entfernen"
        className="shrink-0 p-1 rounded text-text-tertiary opacity-0 group-hover:opacity-100 touch:opacity-100 hover:text-text-primary transition-opacity no-min-tap"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}
